import { useMemo, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { surveys } from '@/api/surveys';
import type {
  SurveyRow, SurveyQuestionRow, SurveyResponseRow,
  AnswerKind, CreateQuestionBody, PatchSurveyBody,
} from '@/api/surveys';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { PageHeader } from '@/components/PageHeader';
import { formatRef } from '@/lib/formatRef';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import {
  ChevronLeft, Plus, Pencil, Trash2, ClipboardList, BarChart3,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { RecordingPicker } from '@/components/ivr/RecordingPicker';

const CAN_MUTATE = ['super_admin', 'reseller_owner', 'office_manager'];

export function SurveyDetail() {
  const { id } = useParams();
  const user = useCurrentUser();
  const canMutate = CAN_MUTATE.includes(user.role);
  const [tab, setTab] = useState<'questions' | 'responses'>('questions');

  const { data: survey, isLoading, isError } = useQuery({
    queryKey: ['survey', id],
    queryFn: () => surveys.get(id!),
    enabled: !!id,
  });

  if (!id) return <Navigate to="/surveys" replace />;
  if (isLoading) return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (isError || !survey) return <NotFoundState />;

  return (
    <>
      <div className="mb-2">
        <Link
          to="/surveys"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          <ChevronLeft className="h-3 w-3" />
          All surveys
        </Link>
      </div>

      <PageHeader
        eyebrow={formatRef(survey.ref_number, 'SRV')}
        title={survey.name}
        description={survey.description ?? undefined}
      />

      <SurveyMetaCard survey={survey} canMutate={canMutate} />

      <div className="mt-6 flex items-center gap-1 border-b border-border">
        <TabButton active={tab === 'questions'} onClick={() => setTab('questions')}>
          <ClipboardList className="h-3.5 w-3.5 mr-1.5" />
          Questions · {survey.question_count}
        </TabButton>
        <TabButton active={tab === 'responses'} onClick={() => setTab('responses')}>
          <BarChart3 className="h-3.5 w-3.5 mr-1.5" />
          Responses · {survey.response_count}
        </TabButton>
      </div>

      <div className="mt-5">
        {tab === 'questions'
          ? <QuestionsPanel survey={survey} canMutate={canMutate} />
          : <ResponsesPanel survey={survey} />}
      </div>
    </>
  );
}

function NotFoundState() {
  return (
    <Card>
      <CardContent className="py-16 text-center text-sm text-muted-foreground">
        Survey not found or no access.
      </CardContent>
    </Card>
  );
}

function TabButton({
  active, onClick, children,
}: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center px-3 py-2 text-xs font-semibold uppercase tracking-wider transition-colors',
        'border-b-2 -mb-px',
        active
          ? 'border-primary text-foreground'
          : 'border-transparent text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

// ── Meta card with inline edit ───────────────────────────────────────────────

function SurveyMetaCard({
  survey, canMutate,
}: { survey: SurveyRow; canMutate: boolean }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [points, setPoints] = useState(String(survey.points_on_complete));
  const [active, setActive] = useState(survey.active);

  const save = useMutation({
    mutationFn: (body: PatchSurveyBody) => surveys.update(survey.id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['survey', survey.id] });
      qc.invalidateQueries({ queryKey: ['surveys'] });
      setEditing(false);
    },
  });

  const pts = Number(points);
  const validPts = Number.isFinite(pts) && pts >= 0 && Number.isInteger(pts);

  return (
    <Card className="mt-4">
      <CardContent className="py-5 flex items-center gap-8">
        <Stat label="Kind" value={survey.kind.toUpperCase()} />
        <Divider />
        {editing ? (
          <div className="flex items-center gap-2">
            <Label htmlFor="pts-edit" className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Points
            </Label>
            <Input
              id="pts-edit" type="number" min="0" step="1"
              value={points} onChange={(e) => setPoints(e.target.value)}
              className="w-20 h-7 text-sm font-mono"
            />
          </div>
        ) : (
          <Stat
            label="Points on complete"
            value={survey.points_on_complete.toLocaleString()}
            valueClassName="text-amber-700"
          />
        )}
        <Divider />
        <Stat label="Questions" value={String(survey.question_count)} />
        <Divider />
        <Stat label="Completed responses" value={String(survey.response_count)} />
        <div className="flex-1" />
        {canMutate && (
          editing ? (
            <>
              <div className="flex items-center gap-2 mr-2">
                <Label htmlFor="active-edit" className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {active ? 'Active' : 'Inactive'}
                </Label>
                <Switch
                  id="active-edit"
                  checked={active}
                  onCheckedChange={setActive}
                />
              </div>
              <Button
                size="sm" variant="outline"
                onClick={() => { setPoints(String(survey.points_on_complete)); setActive(survey.active); setEditing(false); }}
                disabled={save.isPending}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={save.isPending || !validPts}
                onClick={() => save.mutate({ points_on_complete: pts, active })}
              >
                {save.isPending ? 'Saving…' : 'Save'}
              </Button>
            </>
          ) : (
            <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
              <Pencil className="h-3.5 w-3.5 mr-1.5" />
              Edit
            </Button>
          )
        )}
      </CardContent>
    </Card>
  );
}

function Stat({
  label, value, valueClassName,
}: { label: string; value: string; valueClassName?: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">{label}</div>
      <div className={cn('font-mono tabular-nums text-base font-semibold', valueClassName)}>{value}</div>
    </div>
  );
}

function Divider() {
  return <div className="h-8 w-px bg-border" />;
}

// ── Questions panel ──────────────────────────────────────────────────────────

function QuestionsPanel({ survey, canMutate }: { survey: SurveyRow; canMutate: boolean }) {
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['survey-questions', survey.id],
    queryFn: () => surveys.listQuestions(survey.id),
  });
  const questions = data?.rows ?? [];

  const remove = useMutation({
    mutationFn: (qid: string) => surveys.removeQuestion(qid),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['survey-questions', survey.id] });
      qc.invalidateQueries({ queryKey: ['survey', survey.id] });
    },
  });

  return (
    <div className="space-y-3">
      {isLoading ? (
        <div className="text-sm text-muted-foreground">Loading questions…</div>
      ) : questions.length === 0 ? (
        <QuestionsEmpty onAdd={canMutate ? () => setAddOpen(true) : undefined} />
      ) : (
        <div className="space-y-3">
          {questions.map((q, i) => (
            <QuestionCard
              key={q.id}
              index={i + 1}
              question={q}
              canMutate={canMutate}
              onDelete={() => remove.mutate(q.id)}
              removing={remove.isPending}
            />
          ))}
          {canMutate && (
            <div className="pt-2">
              <Button
                size="sm" variant="outline"
                onClick={() => setAddOpen(true)}
                className="w-full border-dashed"
              >
                <Plus className="h-3.5 w-3.5 mr-1.5" />
                Add another question
              </Button>
            </div>
          )}
        </div>
      )}

      {canMutate && (
        <AddQuestionDialog
          surveyId={survey.id}
          customerId={survey.customer_id}
          nextSortOrder={questions.length}
          open={addOpen}
          onOpenChange={setAddOpen}
        />
      )}
    </div>
  );
}

function QuestionsEmpty({ onAdd }: { onAdd?: () => void }) {
  return (
    <Card>
      <CardContent className="py-12 text-center">
        <ClipboardList className="mx-auto h-8 w-8 text-slate-300 mb-3" />
        <p className="text-sm font-medium text-slate-500">No questions yet</p>
        <p className="mt-1 text-xs text-muted-foreground max-w-sm mx-auto">
          Add at least one question before pointing an IVR survey node at this survey.
        </p>
        {onAdd && (
          <Button size="sm" className="mt-4" onClick={onAdd}>
            <Plus className="h-4 w-4 mr-1.5" />
            Add the first question
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function QuestionCard({
  index, question, canMutate, onDelete, removing,
}: {
  index: number;
  question: SurveyQuestionRow;
  canMutate: boolean;
  onDelete: () => void;
  removing: boolean;
}) {
  return (
    <article className="relative rounded-lg border border-border bg-white hover:shadow-sm transition-shadow overflow-hidden">
      <div className="grid grid-cols-[3rem_minmax(0,1fr)_auto] gap-4 p-5">
        <div className="font-mono font-bold text-2xl text-slate-300 tabular-nums leading-none pt-0.5">
          {String(index).padStart(2, '0')}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <AnswerKindChip kind={question.answer_kind} />
            {question.required && (
              <span className="text-[10px] font-semibold uppercase tracking-wider text-rose-700">
                Required
              </span>
            )}
          </div>
          <p className="text-sm leading-relaxed text-foreground">{question.question_text}</p>
          {question.recording_id && (
            <p className="mt-2 text-[11px] font-mono text-muted-foreground">
              audio: {question.recording_id.slice(0, 8)}…
            </p>
          )}
        </div>
        {canMutate && (
          <div className="flex items-start">
            <Button
              size="icon" variant="ghost"
              onClick={onDelete} disabled={removing}
              aria-label={`Delete question ${index}`}
              className="h-7 w-7"
            >
              <Trash2 className="h-3.5 w-3.5 text-slate-400" />
            </Button>
          </div>
        )}
      </div>
    </article>
  );
}

const ANSWER_KIND_STYLES: Record<AnswerKind, string> = {
  dtmf_single: 'bg-sky-50 text-sky-700 ring-sky-200',
  dtmf_multi:  'bg-indigo-50 text-indigo-700 ring-indigo-200',
  sms_text:    'bg-violet-50 text-violet-700 ring-violet-200',
  sms_dtmf:    'bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-200',
};

function AnswerKindChip({ kind }: { kind: AnswerKind }) {
  return (
    <span className={cn(
      'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ring-1 w-fit',
      ANSWER_KIND_STYLES[kind],
    )}>
      {kind.replace('_', ' ')}
    </span>
  );
}

function AddQuestionDialog({
  surveyId, customerId, nextSortOrder, open, onOpenChange,
}: {
  surveyId: string;
  customerId: string;
  nextSortOrder: number;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [kind, setKind] = useState<AnswerKind>('dtmf_single');
  const [required, setRequired] = useState(true);
  const [recordingId, setRecordingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: (body: CreateQuestionBody) => surveys.createQuestion(surveyId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['survey-questions', surveyId] });
      qc.invalidateQueries({ queryKey: ['survey', surveyId] });
      setText(''); setKind('dtmf_single'); setRequired(true); setRecordingId(null); setError(null);
      onOpenChange(false);
    },
    onError: (err: unknown) =>
      setError(err instanceof Error ? err.message : 'Create failed.'),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add question · {String(nextSortOrder + 1).padStart(2, '0')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1">
            <Label htmlFor="q-text">Question text</Label>
            <Textarea
              id="q-text" rows={3}
              value={text} onChange={(e) => setText(e.target.value)}
              placeholder="e.g. How would you rate your experience from 1 to 5?"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Answer kind</Label>
              <Select value={kind} onValueChange={(v) => setKind(v as AnswerKind)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="dtmf_single">DTMF · single digit</SelectItem>
                  <SelectItem value="dtmf_multi">DTMF · multi-digit (# term)</SelectItem>
                  <SelectItem value="sms_text">SMS · free text</SelectItem>
                  <SelectItem value="sms_dtmf">SMS · numeric</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1 flex items-end gap-3 pb-1">
              <div className="flex items-center gap-2">
                <Switch id="req" checked={required} onCheckedChange={setRequired} />
                <Label htmlFor="req" className="text-sm cursor-pointer">Required</Label>
              </div>
            </div>
          </div>
          <div className="space-y-1">
            <Label>Recording (optional)</Label>
            <RecordingPicker
              value={recordingId}
              onChange={(id) => setRecordingId(id)}
              customerId={customerId}
            />
            <p className="text-[11px] text-muted-foreground">
              In-call playback of the question. Without this, callers hear silence until TTS fallback lands.
            </p>
          </div>
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={create.isPending}>Cancel</Button>
          </DialogClose>
          <Button
            disabled={create.isPending || !text.trim()}
            onClick={() => create.mutate({
              question_text: text.trim(),
              answer_kind: kind,
              required,
              recording_id: recordingId,
              sort_order: nextSortOrder,
            })}
          >
            {create.isPending ? 'Adding…' : 'Add question'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Responses panel ──────────────────────────────────────────────────────────

function ResponsesPanel({ survey }: { survey: SurveyRow }) {
  const { data: questionsData } = useQuery({
    queryKey: ['survey-questions', survey.id],
    queryFn: () => surveys.listQuestions(survey.id),
  });
  const { data: responsesData, isLoading } = useQuery({
    queryKey: ['survey-responses', survey.id],
    queryFn: () => surveys.listResponses(survey.id, { completed: true, limit: 500 }),
  });

  const questions = questionsData?.rows ?? [];
  const responses = responsesData?.rows ?? [];

  // Build distribution per question
  const distributions = useMemo(() => {
    const out = new Map<string, Map<string, number>>();
    for (const q of questions) out.set(q.id, new Map());
    for (const r of responses) {
      for (const [qid, val] of Object.entries(r.answers)) {
        if (!out.has(qid)) continue;
        const valStr = String(val ?? '');
        const bucket = out.get(qid)!;
        bucket.set(valStr, (bucket.get(valStr) ?? 0) + 1);
      }
    }
    return out;
  }, [questions, responses]);

  if (isLoading) {
    return <div className="text-sm text-muted-foreground">Loading responses…</div>;
  }
  if (responses.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <BarChart3 className="mx-auto h-8 w-8 text-slate-300 mb-3" />
          <p className="text-sm font-medium text-slate-500">No completed responses yet</p>
          <p className="mt-1 text-xs text-muted-foreground max-w-sm mx-auto">
            Once callers walk through your survey, completed runs appear here with an answer distribution per question.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Overview</CardTitle>
          <CardDescription>
            {responses.length} completed response{responses.length === 1 ? '' : 's'}.
          </CardDescription>
        </CardHeader>
      </Card>

      {questions.map((q, i) => (
        <QuestionDistribution
          key={q.id}
          index={i + 1}
          question={q}
          distribution={distributions.get(q.id) ?? new Map()}
          total={responses.length}
        />
      ))}
    </div>
  );
}

function QuestionDistribution({
  index, question, distribution, total,
}: {
  index: number;
  question: SurveyQuestionRow;
  distribution: Map<string, number>;
  total: number;
}) {
  const entries = Array.from(distribution.entries())
    .filter(([val]) => val.length > 0)
    .sort((a, b) => b[1] - a[1]);
  const maxCount = entries.reduce((m, [, c]) => Math.max(m, c), 0);
  const emptyAnswers = (distribution.get('') ?? 0);

  return (
    <article className="rounded-lg border border-border bg-white overflow-hidden">
      <div className="grid grid-cols-[3rem_minmax(0,1fr)] gap-4 p-5">
        <div className="font-mono font-bold text-xl text-slate-300 tabular-nums leading-none pt-1">
          {String(index).padStart(2, '0')}
        </div>
        <div className="min-w-0">
          <div className="mb-3 flex items-center gap-2">
            <AnswerKindChip kind={question.answer_kind} />
            {question.required && (
              <span className="text-[10px] font-semibold uppercase tracking-wider text-rose-700">
                Required
              </span>
            )}
          </div>
          <p className="text-sm mb-4 text-foreground">{question.question_text}</p>

          {entries.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">
              No answers captured yet.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {entries.map(([val, count]) => {
                const pct = total > 0 ? (count / total) * 100 : 0;
                const relWidth = maxCount > 0 ? (count / maxCount) * 100 : 0;
                return (
                  <li key={val} className="grid grid-cols-[4rem_minmax(0,1fr)_3.5rem] items-center gap-3">
                    <span className="font-mono text-sm tabular-nums text-right truncate">{val}</span>
                    <div className="relative h-5 rounded bg-slate-100 overflow-hidden">
                      <div
                        className="absolute inset-y-0 left-0 bg-primary/70 flex items-center px-1.5"
                        style={{ width: `${relWidth}%` }}
                      />
                    </div>
                    <span className="text-xs text-muted-foreground tabular-nums">
                      {count} · {pct.toFixed(0)}%
                    </span>
                  </li>
                );
              })}
            </ul>
          )}

          {emptyAnswers > 0 && (
            <p className="mt-3 text-[11px] italic text-muted-foreground">
              {emptyAnswers} response{emptyAnswers === 1 ? '' : 's'} left this question blank.
            </p>
          )}
        </div>
      </div>
    </article>
  );
}
